import { type ChangeRequestDto, CHANGE_REQUEST_NOTE_MAX, type MeDto, type Readiness, type TaskDto } from '@bqcrm/shared';
import { can } from '../../auth';
import { useTaskTypeLabel } from '../../data';
import { Alert, Anchor, Badge, Button, Divider, Group, Menu, Modal, Stack, Table, Text, Textarea } from '@mantine/core';
import { IconCalendarEvent, IconDots, IconFileAlert, IconLock } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../api';
import { ReadinessBadge, TaskStatusBadge, TradeBadge } from '../../components/Badges';
import { notifyError, notifyOk } from '../../notify';
import { formatDateTime } from '../../format';
import { ConflictList, dayLabel, FleetWarnings, ReasonDialog, termText, usePeople, useTeams } from './common';
import { FieldSection } from './FieldSection';
import { PlanDialog } from './PlanDialog';


/** Szczegóły zadania: termin i pracownicy na dzień, konflikty, dane z terenu (protokoły, zdjęcia); zmiana terminu, cofnięcie, anulowanie. */
/**
 * Prośba klienta ze strefy o zmianę terminu (docs/18): treść prośby, „Zmień termin” (przesunięcie spełnia prośbę i wysyła
 * klientowi e-mail z nowym terminem) albo odpowiedź bez zmiany terminu (strefa i e-mail).
 */
function ChangeRequestNote({ taskId, onPlan }: { taskId: string; onPlan?: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['task-change-requests', taskId], queryFn: () => api<ChangeRequestDto[]>(`/tasks/${taskId}/change-requests`) });
  const [answering, setAnswering] = useState(false);
  const [message, setMessage] = useState('');
  const answer = useMutation({
    mutationFn: () => api<ChangeRequestDto[]>(`/tasks/${taskId}/change-requests/answer`, { method: 'POST', body: { message } }),
    onSuccess: (rows) => {
      qc.setQueryData(['task-change-requests', taskId], rows);
      for (const k of ['process-inbox', 'process-inbox-count', 'case-process', 'case']) void qc.invalidateQueries({ queryKey: [k] });
      setAnswering(false);
      setMessage('');
      notifyOk('Wysłano odpowiedź klientowi — prośba zamknięta');
    },
    onError: notifyError,
  });
  const open = q.data?.find((r) => r.status === 'otwarta');
  if (!open) return null;
  return (
    <Alert color="orange" title="Klient prosi o zmianę terminu">
      <Text size="sm">„{open.note}”</Text>
      <Text size="xs" c="dimmed" mb="xs">
        ze strefy klienta, {formatDateTime(open.createdAt)}. Przesunięcie terminu spełnia prośbę — klient dostanie e-mail z nowym terminem.
      </Text>
      <Group gap="xs">
        {onPlan && (
          <Button size="xs" onClick={onPlan}>
            Zmień termin
          </Button>
        )}
        <Button size="xs" variant="default" onClick={() => setAnswering(true)}>
          Odpowiedz bez zmiany terminu
        </Button>
      </Group>
      <Modal opened={answering} onClose={() => setAnswering(false)} title="Odpowiedź dla klienta">
        <Stack>
          <Textarea
            label="Odpowiedź (klient zobaczy ją w strefie i w e-mailu)"
            placeholder="np. Najbliższy wolny termin to 20.11 — prosimy o kontakt telefoniczny"
            autosize
            minRows={3}
            maxLength={CHANGE_REQUEST_NOTE_MAX}
            value={message}
            onChange={(e) => setMessage(e.currentTarget.value)}
            data-autofocus
          />
          <Text size="xs" c="dimmed">
            Termin pracy zostaje bez zmian, a prośba się zamyka.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAnswering(false)}>
              Anuluj
            </Button>
            <Button loading={answer.isPending} disabled={message.trim().length < 3} onClick={() => answer.mutate()}>
              Wyślij odpowiedź
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Alert>
  );
}

export function TaskPanel({ me, taskId, onClose }: { me: MeDto; taskId: string; onClose: () => void }) {
  const typeLabel = useTaskTypeLabel();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['task', taskId], queryFn: () => api<TaskDto>(`/tasks/${taskId}`) });
  const exec = q.data?.taskType === 'montaz' || q.data?.taskType === 'uruchomienie';
  const mat = useQuery({
    queryKey: ['tasks-readiness', taskId],
    queryFn: () => api<Record<string, Readiness | null>>(`/tasks-readiness?ids=${taskId}`),
    enabled: exec && can(me, 'zapotrzebowania.podglad'),
  });
  const people = usePeople();
  const teams = useTeams();
  const [planning, setPlanning] = useState(false);
  const [reason, setReason] = useState<'unplan' | 'cancel' | null>(null);
  const name = (id: string) => people.data?.find((p) => p.id === id)?.fullName ?? '—';
  const act = useMutation({
    mutationFn: (v: { kind: 'unplan' | 'cancel'; reason: string }) => api<TaskDto>(`/tasks/${taskId}/${v.kind}`, { method: 'POST', body: { reason: v.reason } }),
    onSuccess: (t) => {
      qc.setQueryData(['task', taskId], t);
      for (const k of ['planning-board', 'planning-queue', 'calendar', 'case-tasks', 'service-ticket', 'service-tickets']) void qc.invalidateQueries({ queryKey: [k] });
      notifyOk(t.status === 'anulowane' ? 'Anulowano zadanie' : 'Cofnięto do kolejki');
      onClose();
    },
    onError: notifyError,
  });
  const t = q.data;
  const days = t ? [...new Set(t.assignments.map((a) => a.day))] : [];
  const team = t?.teamId ? teams.data?.find((x) => x.id === t.teamId) : null;

  return (
    <Modal opened onClose={onClose} title={t ? t.title : 'Zadanie'} size="lg">
      {t && (
        <Stack gap="sm">
          {t.caseId && can(me, 'planowanie.edycja') && <ChangeRequestNote taskId={taskId} onPlan={t.can.plan ? () => setPlanning(true) : undefined} />}
          <Group justify="space-between" align="flex-start">
            <Group gap="xs">
              <TaskStatusBadge status={t.status} />
              <Badge variant="outline" color="gray">
                {typeLabel(t)}
              </Badge>
              {t.scopes.map((s) => (
                <TradeBadge key={s.id} code={s.tradeCode} />
              ))}
              {mat.data?.[taskId] &&
                (t.caseId ? (
                  <Anchor component={Link} to={`/sprawy/${t.caseId}?zakladka=materialy`} underline="never" title="Gotowość materiałowa">
                    <ReadinessBadge readiness={mat.data[taskId]!} />
                  </Anchor>
                ) : (
                  <ReadinessBadge readiness={mat.data[taskId]!} />
                ))}
              {t.conditional && (
                <Badge color="orange" leftSection={<IconLock size={12} />}>
                  start zablokowany
                </Badge>
              )}
            </Group>
            {t.can.plan && (
              <Group gap="xs">
                <Button size="xs" leftSection={<IconCalendarEvent size={14} />} onClick={() => setPlanning(true)}>
                  {t.dateFrom ? 'Zmień plan' : 'Zaplanuj'}
                </Button>
                <Menu position="bottom-end">
                  <Menu.Target>
                    <Button size="xs" variant="default" px={8} aria-label="Więcej">
                      <IconDots size={14} />
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {t.status === 'zaplanowane' && <Menu.Item onClick={() => setReason('unplan')}>Cofnij do kolejki</Menu.Item>}
                    <Menu.Item color="red" onClick={() => setReason('cancel')}>
                      Anuluj zadanie
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )}
          </Group>

          <Table fz="sm" verticalSpacing={4} withRowBorders={false}>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td w={110} c="dimmed">
                  Numer
                </Table.Td>
                <Table.Td>{t.number}</Table.Td>
              </Table.Tr>
              {(t.caseId || t.ticketId) && (
                <Table.Tr>
                  <Table.Td c="dimmed">{t.ticketId ? 'Zgłoszenie' : 'Sprawa'}</Table.Td>
                  <Table.Td>
                    <Anchor component={Link} to={t.ticketId ? `/serwis?zgloszenie=${t.ticketId}` : `/sprawy/${t.caseId}?zakladka=prace`} size="sm">
                      {t.ticketNumber ?? t.caseNumber}
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              )}
              <Table.Tr>
                <Table.Td c="dimmed">Klient</Table.Td>
                <Table.Td>
                  {t.clientName ?? '—'}
                  {t.siteAddress ? ` · ${t.siteAddress}` : ''}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Td c="dimmed">Termin</Table.Td>
                <Table.Td>
                  {t.segments.length > 1 ? (
                    <Stack gap={2}>
                      {t.segments.map((sg, i) => (
                        <Text key={sg.id} size="sm">
                          {i + 1}. <b>{termText(sg)}</b>
                          {sg.teamId ? ` · ${teams.data?.find((x) => x.id === sg.teamId)?.name ?? ''}` : ' · wybrani pracownicy'}
                        </Text>
                      ))}
                    </Stack>
                  ) : (
                    <b>{termText(t)}</b>
                  )}
                  {t.segments.length <= 1 && team ? ` · ${team.name}` : ''}
                  {t.plannedByName ? <Text span size="xs" c="dimmed">{` · zaplanował(a) ${t.plannedByName}`}</Text> : null}
                </Table.Td>
              </Table.Tr>
              {days.length > 0 && (
                <Table.Tr>
                  <Table.Td c="dimmed" style={{ verticalAlign: 'top' }}>
                    Pracownicy
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={2}>
                      {days.map((d) => (
                        <Group key={d} gap={4}>
                          {days.length > 1 && (
                            <Text size="xs" w={56} c={dayLabel(d).weekend ? 'red' : 'dimmed'}>
                              {dayLabel(d).wd} {dayLabel(d).dm}
                            </Text>
                          )}
                          {t.assignments
                            .filter((a) => a.day === d)
                            .map((a) => (
                              <Badge key={a.userId} component={Link} to={`/pracownicy/${a.userId}`} size="sm" variant={a.source === 'ekipa' ? 'light' : 'outline'} color={a.state === 'faktyczny' ? 'gray' : 'blue'} style={{ cursor: 'pointer' }}>
                                {name(a.userId)}
                                {a.state === 'faktyczny' ? ' ✓' : ''}
                              </Badge>
                            ))}
                        </Group>
                      ))}
                    </Stack>
                  </Table.Td>
                </Table.Tr>
              )}
              {t.description && (
                <Table.Tr>
                  <Table.Td c="dimmed">Opis</Table.Td>
                  <Table.Td>{t.description}</Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>

          {t.conditional && (
            <Alert color="orange" variant="light" icon={<IconLock size={16} />}>
              Zaplanowane warunkowo — warunki startu (umowa, zaliczka) nie są spełnione. Ekipa nie rozpocznie zadania, dopóki nie zostaną spełnione lub nie zostanie udzielone odstępstwo.
            </Alert>
          )}
          {/* QA qa10301331 P8: ślad odblokowania pracy (zostaje po starcie) */}
          {!t.conditional && t.startReadyAt && (
            <Text size="sm" c="teal">
              Warunki startu spełnione {formatDateTime(t.startReadyAt)}
            </Text>
          )}
          {/* QA qa10301331 P11 / qa10302243 P2, P9: aneks do zakresu tej pracy — tekst zależny od dat wydania i podpisu */}
          {!!t.annexAfterPlan?.length && (
            <Alert color="yellow" variant="light" icon={<IconFileAlert size={16} />}>
              <Stack gap={4}>
                {t.annexAfterPlan.map((a) => {
                  const link = (
                    <Anchor component={Link} to={`/umowy/${a.id}`} size="sm">
                      {a.number ?? '(bez numeru)'}
                    </Anchor>
                  );
                  if (a.status === 'podpisany')
                    return (
                      <Text key={a.id} size="sm">
                        Aneks {link} podpisany{a.signedAt ? ` ${formatDateTime(a.signedAt)}` : ''} po zaplanowaniu tej pracy — jego prace weszły do tego zadania. Sprawdź, czy ekipa ma je wykonać w tej wizycie.
                      </Text>
                    );
                  if (a.issuedAfterPlan === false)
                    return (
                      <Text key={a.id} size="sm">
                        Do umowy jest aneks {link} czekający na podpis — po podpisie jego prace wejdą do tego zadania.
                      </Text>
                    );
                  return (
                    <Text key={a.id} size="sm">
                      Aneks {link} czeka na podpis{a.issuedAt ? ` (wydany ${formatDateTime(a.issuedAt)})` : ''} — wydany po zaplanowaniu tej pracy, może zmienić zakres. Sprawdź, czy prace z aneksu ująć w tej wizycie.
                    </Text>
                  );
                })}
              </Stack>
            </Alert>
          )}
          {t.conflicts.length > 0 && (
            <Alert color="red" title="Konflikty">
              <ConflictList conflicts={t.conflicts} />
            </Alert>
          )}
          {/* Flota (docs/19 §6): auto ekipy / osoby niedostępne w dniach zadania — informacja */}
          <FleetWarnings warnings={t.fleetWarnings} note="Zadanie nie jest przesuwane — sprawdź auto zastępcze albo zmień termin." />
          {t.status !== 'do_zaplanowania' && (
            <>
              <Divider />
              <FieldSection taskId={t.id} />
            </>
          )}
        </Stack>
      )}
      {planning && t && (
        <PlanDialog
          me={me}
          task={t}
          onClose={(saved) => {
            setPlanning(false);
            if (saved) onClose();
          }}
        />
      )}
      {reason && (
        <ReasonDialog
          title={reason === 'cancel' ? 'Anulowanie zadania' : 'Cofnięcie do kolejki'}
          label="Powód"
          confirm={reason === 'cancel' ? 'Anuluj zadanie' : 'Cofnij do kolejki'}
          color={reason === 'cancel' ? 'red' : undefined}
          busy={act.isPending}
          onClose={() => setReason(null)}
          onConfirm={(r) => act.mutate({ kind: reason, reason: r })}
        />
      )}
    </Modal>
  );
}
